<template>
    <div class="competition-edit">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <panel-info-box v-if="submitSuccess">
                <img src="../assets/images/icons/checkmark-circle.svg" style="max-height: 48px" />
                <p class="text-lg"><b>Dane zostały poprawnie zapisane</b></p>
            </panel-info-box>

            <panel-competition-register-form
                :inputs.sync="inputs"
                :anonymous.sync="anonymous"
                :classifications="classificationList"
                :saving="saving"
                @add="addInput"
                @remove="removeInput"
                @copy="copyInput"
                @move-up="moveUp"
                @move-down="moveDown"
                @save="save"
            />
        </template>
    </div>
</template>

<script>
    import axios from '@/api';
    import PanelInfoBox from '@/components/PanelInfoBox';
    import PanelCompetitionRegisterForm from '@/components/PanelCompetitionRegisterForm';
    import {
        createEmptyInput,
        mapApiInputToForm,
        mapFormInputToApi,
        nextInputKey,
    } from '@/constants/competitionRegisterInputs';
    import { mapActions, mapGetters } from 'vuex';
    import {
        READ_COMPETITION_CLASSIFICATIONS,
        READ_PANEL_COMPETITION,
        READ_PANEL_COMPETITION_PLAYERS_INPUTS,
        UPDATE_PANEL_COMPETITION_INPUTS,
    } from '@/store/actions.type';

    export default {
        name: 'PanelCompetitionSettingRegister',
        components: {
            PanelInfoBox,
            PanelCompetitionRegisterForm,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                saving: false,
                submitSuccess: false,
                inputs: [],
                anonymous: 'no',
                classificationList: [],
            };
        },
        computed: {
            ...mapGetters(['classifications', 'competitionsSettings']),
            competitionId() {
                return this.competition.id;
            },
            competitionSetting() {
                return this.competitionsSettings[this.competitionId] || {};
            },
            defaultClassificationIds() {
                return this.classificationList.map((item) => item.id);
            },
        },
        watch: {
            submitSuccess(isSuccess) {
                if (isSuccess) {
                    document.querySelector('body').scrollIntoView({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            },
        },
        async created() {
            await this.init();
        },
        methods: {
            ...mapActions([
                READ_COMPETITION_CLASSIFICATIONS,
                READ_PANEL_COMPETITION,
                READ_PANEL_COMPETITION_PLAYERS_INPUTS,
                UPDATE_PANEL_COMPETITION_INPUTS,
            ]),
            resolveAnonymousFlag() {
                const setting = this.competitionSetting;
                const value =
                    setting.anonymous ??
                    setting.anonimous ??
                    this.competition.anonymous ??
                    this.competition.anonimous ??
                    false;
                return value === true || value === 1 || value === '1' || value === 'yes' ? 'yes' : 'no';
            },
            loadClassificationList() {
                const competitionId = Number(this.competitionId);
                const all = Object.values(this.classifications || {});
                this.classificationList = all.filter((item) => {
                    const fk = item.competition;
                    if (fk == null) {
                        return false;
                    }
                    if (typeof fk === 'object') {
                        return Number(fk.id) === competitionId;
                    }
                    return Number(fk) === competitionId;
                });
            },
            async init({ silent = false } = {}) {
                if (!silent) {
                    this.ready = false;
                }
                try {
                    const [apiInputs] = await Promise.all([
                        this.READ_PANEL_COMPETITION_PLAYERS_INPUTS(this.competitionId),
                        this.READ_COMPETITION_CLASSIFICATIONS(this.competitionId),
                    ]);
                    this.loadClassificationList();
                    const list = Array.isArray(apiInputs) ? apiInputs : [];
                    const sorted = [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
                    this.inputs = sorted.map((input) => mapApiInputToForm(input, this.defaultClassificationIds));
                    this.anonymous = this.resolveAnonymousFlag();
                } catch (e) {
                    this.loadClassificationList();
                    this.inputs = [];
                    this.anonymous = this.resolveAnonymousFlag();
                } finally {
                    this.ready = true;
                }
            },
            addInput() {
                this.inputs = [...this.inputs, createEmptyInput(this.defaultClassificationIds)];
            },
            removeInput(index) {
                this.inputs = this.inputs.filter((_, i) => i !== index);
            },
            copyInput(index) {
                const source = this.inputs[index];
                if (!source) {
                    return;
                }
                const copy = {
                    ...source,
                    _key: nextInputKey(),
                    id: 0,
                    options: [...(source.options || [])],
                    classificationIds: [...(source.classificationIds || [])],
                };
                const inputs = [...this.inputs];
                inputs.splice(index + 1, 0, copy);
                this.inputs = inputs;
            },
            moveUp(index) {
                if (index <= 0) {
                    return;
                }
                const inputs = [...this.inputs];
                const tmp = inputs[index - 1];
                inputs[index - 1] = inputs[index];
                inputs[index] = tmp;
                this.inputs = inputs;
            },
            moveDown(index) {
                if (index >= this.inputs.length - 1) {
                    return;
                }
                const inputs = [...this.inputs];
                const tmp = inputs[index + 1];
                inputs[index + 1] = inputs[index];
                inputs[index] = tmp;
                this.inputs = inputs;
            },
            async save() {
                this.saving = true;
                this.submitSuccess = false;
                try {
                    const payload = this.inputs.map((input, position) => mapFormInputToApi(input, position));
                    await Promise.all([
                        this.UPDATE_PANEL_COMPETITION_INPUTS({
                            id: this.competitionId,
                            inputs: payload,
                        }),
                        axios.put(`panel/competitions/${this.competitionId}/settings`, {
                            anonymous: this.anonymous === 'yes',
                        }),
                    ]);
                    await this.READ_PANEL_COMPETITION(this.competitionId);
                    await this.init({ silent: true });
                    this.submitSuccess = true;
                } catch (e) {
                    // Keep form state for correction.
                } finally {
                    this.saving = false;
                }
            },
        },
    };
</script>

<style scoped>
    .competition-edit {
        width: 100%;
        margin-left: 0;
    }
    @media (min-width: 992px) {
        .competition-edit {
            margin-left: 30px;
        }
    }
    .admin-header__loading-state {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
    }
</style>
